import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Code as CodeIcon,
  GitHub as GitHubIcon,
  Link as LinkIcon
} from "@mui/icons-material";
import projects from "../data/projects";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projects.find((p) => p.id === id);

  if (!project) {
    return (
      <Box sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh", py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4">Project not found</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/profile")}
            sx={{ mt: 2 }}
          >
            Back to Profile
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh", py: 8 }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/profile")}
          sx={{
            mb: 3,
            textTransform: "none",
            fontWeight: 600,
            color: "#1976d2"
          }}
        >
          Back to Profile
        </Button>

        <Paper
          elevation={0}
          sx={{ p: 5, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
        >
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              icon={<CodeIcon />}
              label={project.role || "Developer"}
              sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}
            />
            {project.status && (
              <Chip
                label={project.status}
                sx={{
                  bgcolor: project.status === "In Progress" ? "#fff3e0" : "#e8f5e9",
                  color: project.status === "In Progress" ? "#f57c00" : "#2e7d32"
                }}
              />
            )}
          </Stack>

          <Typography variant="h3" fontWeight="bold" gutterBottom>
            {project.title}
          </Typography>

          {project.institution && (
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {project.institution}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            {project.description}
          </Typography>

          {project.achievements && project.achievements.length > 0 && (
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Key Achievements
              </Typography>
              <Box component="ul" sx={{ mb: 4, pl: 3 }}>
                {project.achievements.map((achievement, idx) => (
                  <Typography component="li" key={idx} variant="body1" sx={{ mb: 1 }}>
                    {achievement}
                  </Typography>
                ))}
              </Box>
            </>
          )}

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Technology Stack
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 4 }}>
            {project.techStack.map((tech, idx) => (
              <Chip
                key={idx}
                label={tech}
                variant="outlined"
                sx={{ mb: 1, fontSize: "0.875rem" }}
              />
            ))}
          </Stack>
          {project.highlights && project.highlights.length > 0 && (
  <>
    <Typography variant="h5" fontWeight="bold" gutterBottom>
      Highlights
    </Typography>

    <Stack spacing={1.5} sx={{ mb: 4 }}>
      {project.highlights.map((item, idx) => (
        <Paper
          key={idx}
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: "#fafafa"
          }}
        >
          <Typography variant="body1">• {item}</Typography>
        </Paper>
      ))}
    </Stack>
  </>
)}

          {(project.url || project.liveLink) && (
            <>
              <Divider sx={{ my: 3 }} />
              <Stack direction="row" spacing={2}>
                {project.url && (
                  <Button
                    variant="contained"
                    startIcon={<GitHubIcon />}
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: "#24292e",
                      "&:hover": { bgcolor: "#1a1f23" }
                    }}
                  >
                    View on GitHub
                  </Button>
                )}
                {project.liveLink && (
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    href={project.liveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: "#1976d2",
                      "&:hover": { bgcolor: "#1565c0" }
                    }}
                  >
                    View Live Demo
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ProjectDetail;